import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Sollabweichung, Sollgeraeteliste, Sollgeraetezeile, Sollseite,
} from '../api';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, formatiereZahl, type Spalte,
} from '../muster';

/**
 * Die Sollseite des Gewerks: die Geräteliste Heizung + Kälte des
 * Fachplaners (Teuber + Viel GmbH, 26.06.2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WARUM SIE NEBEN DER GERÄTELISTE STEHT UND NICHT DARIN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Der Reiter „Geräteliste" zeigt, was im MODELL steht — gemessen, aus der
 * Aufnahme. Diese Seite zeigt, was gebaut WIRD — zugesagt, aus der Vorlage
 * des Fachplaners. Beides in eine Tabelle zu mischen hieße, eine gemessene
 * Zahl neben eine zugesagte zu stellen, ohne dass man den Unterschied noch
 * sieht. Genau dieser Unterschied ist der Zweck der Seite.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ROHTEXT UND ZAHL STEHEN NEBENEINANDER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Größenangabe steht in der Vorlage als Text: „1.000kW", „12,5m³",
 * „bis DN80". Die Spalte zeigt beides — den Rohtext als Beleg und die
 * gelesene Zahl als Rechengröße. „1.000kW" sind 1.000 kW und nicht 1; an
 * genau dieser Stelle ist im Projekt schon einmal ein Faktor 1000
 * entstanden.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DIE AKS-NUMMER IST HIER EINE ANZEIGE UND KEIN WERT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Die Vorlage trägt AKS-Adressen, die Datenbank trägt AKS-Adressen. Dass
 * beide zusammengehören müssen, ist wahr — und trotzdem nicht die
 * Entscheidung dieser Seite. Die AKS ist seit dem 20.09.2026 eingefroren
 * (F29). Was hier steht, ist die Vorlage für diese Entscheidung und kein
 * Schreibweg: Es gibt auf dieser Seite keinen Knopf, der etwas ändert.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

// TH-2b: formatiereZahl statt toLocaleString (TX-STANDARD S1.7); feste Stellen wie bisher.
const zahl = (n: number | null | undefined, stellen = 0) =>
  n === null || n === undefined ? '—' : formatiereZahl(n, stellen);

/** Wie ein Weg heißt, wenn man ihn jemandem erklären muss. */
const WEG_TEXT: Record<string, string> = {
  aks: 'über die AKS-Nummer',
  standort: 'über Standort und Kategorie',
  name: 'über den Gerätenamen',
};

export function HeizungSollgeraete() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [liste, setListe] = useState<Sollgeraeteliste | null>(null);
  const [soll, setSoll] = useState<Sollseite | null>(null);
  const [abw, setAbw] = useState<Sollabweichung | null>(null);
  const [quelle, setQuelle] = useState('');
  const [nurOffen, setNurOffen] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    Promise.all([
      api.sollgeraete(projekt.id, {
        quelle: quelle || undefined,
        weg: nurOffen ? 'offen' : undefined,
      }),
      api.sollgeraeteSollseite(projekt.id),
      api.sollgeraeteAbweichung(projekt.id),
    ])
      .then(([l, s, a]) => { setListe(l); setSoll(s); setAbw(a); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id, quelle, nurOffen]);

  const spalten: Spalte<Sollgeraetezeile>[] = useMemo(() => [
    {
      schluessel: 'kennung', titel: 'ID', immerSichtbar: true, breite: '80px',
      wert: (z) => z.kennung, inhalt: (z) => <code>{z.kennung}</code>,
    },
    {
      schluessel: 'aks', titel: 'AKS der Vorlage', wert: (z) => z.aksNummer ?? '',
      inhalt: (z) => <code className="schwach">{z.aksNummer ?? '—'}</code>,
    },
    { schluessel: 'abschnitt', titel: 'Abschnitt', wert: (z) => z.abschnitt ?? '' },
    { schluessel: 'system', titel: 'System', wert: (z) => z.system ?? '' },
    { schluessel: 'kategorie', titel: 'Kategorie', wert: (z) => z.kategorie ?? '' },
    {
      schluessel: 'geraet', titel: 'Gerät / Funktion', immerSichtbar: true,
      wert: (z) => z.geraetFunktion ?? '',
    },
    { schluessel: 'standort', titel: 'Standort', wert: (z) => z.standort ?? '' },
    {
      schluessel: 'menge', titel: 'Menge', art: 'zahl', wert: (z) => z.menge ?? 0,
      inhalt: (z) => <>{zahl(z.menge)} {z.einheit ?? ''}</>,
    },
    {
      // Rohtext UND Zahl in einer Spalte, untereinander: Wer die Zahl
      // anzweifelt, sieht den Beleg ohne zweiten Klick.
      schluessel: 'groesse', titel: 'Größe (Rohtext / Zahl)',
      wert: (z) => z.groesseWert ?? 0,
      inhalt: (z) => (z.groesseText === null ? <span className="schwach">—</span> : (
        <>
          <code>{z.groesseText}</code>
          <div className="schwach" style={{ fontSize: 11 }}>
            {z.groesseVergleich ? `${z.groesseVergleich} ` : ''}
            {zahl(z.groesseWert, z.groesseWert !== null
              && !Number.isInteger(z.groesseWert) ? 1 : 0)}
            {' '}{z.groesseEinheit ?? ''}
          </div>
        </>
      )),
    },
    {
      schluessel: 'leistung', titel: 'kW', art: 'zahl',
      wert: (z) => z.leistungKw ?? 0,
      inhalt: (z) => <>{zahl(z.leistungKw)}</>,
    },
    {
      schluessel: 'zuordnung', titel: 'im Modell gefunden', art: 'status',
      breite: '280px',
      wert: (z) => (z.zuordnungWeg ? WEG_TEXT[z.zuordnungWeg] ?? z.zuordnungWeg
        : 'nicht gefunden'),
      inhalt: (z) => {
        if (!z.zuordnungWeg) {
          return <StatusBadge zustand="warnung" klein text="nicht gefunden" />;
        }

        const eindeutig = z.zuordnungZielId !== null;

        return (
          <>
            <StatusBadge
              zustand={eindeutig ? 'gut' : 'info'} klein
              text={WEG_TEXT[z.zuordnungWeg] ?? z.zuordnungWeg}
            />
            <div className="schwach" style={{ fontSize: 11 }}>
              {eindeutig
                ? z.zuordnungZielText ?? ''
                : `${z.zuordnungKandidaten} Kandidaten — nicht eindeutig`}
            </div>
          </>
        );
      },
    },
  ], []);

  if (!projekt) return <LoadingState text="Projekt wird gelesen …" />;

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Geräteliste des Fachplaners konnte nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!liste || !soll || !abw) {
    return <LoadingState text="Die Sollseite wird geladen …" zeilen={10} spalten={6} />;
  }

  const lauf = liste.lauf;

  if (!lauf) {
    return (
      <>
        <PageHeader titel="Sollgeräte" />
        <EmptyState
          titel="Die Geräteliste des Fachplaners ist noch nicht eingelesen."
          erklaerung={'Sie wird mit dem Werkzeug `geraeteliste-hk-einlesen '
            + '<projektId> <pdf> --schreiben` eingelesen. Bis dahin gibt es für '
            + 'dieses Gewerk keine Sollseite — und damit keinen Anker, gegen den '
            + 'sich eine gerechnete Last messen ließe.'}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        titel="Sollgeräte — die Geräteliste des Fachplaners"
        beschreibung={`${lauf.dateiname}, ${lauf.seiten} Seiten, gelesen am `
          + `${new Date(lauf.gelesenAm).toLocaleDateString('de-DE')}`}
      />

      <ContentSection
        titel="Die Sollseite"
        beschreibung={'Diese Zahlen sind die Gegenprobe jeder späteren Last- und '
          + 'Auslegungsrechnung. Sie stammen aus der Vorlage des Fachplaners und '
          + 'nicht aus dem Modell — wo beide auseinandergehen, ist das ein Befund '
          + 'und kein Anlass, das eine mit dem anderen zu überschreiben.'}
      >
        <KpiReihe>
          <KpiCard
            titel="Wärmeerzeugung" wert={zahl(soll.waermeerzeugungKw)} einheit="kW"
            fussnote="Übergabestationen Fernwärme"
          />
          <KpiCard
            titel="Kälteerzeugung" wert={zahl(soll.kaelteerzeugungKw)} einheit="kW"
            fussnote="Kältemaschinen inkl. Eisspeicherladung"
          />
          <KpiCard
            titel="Rückkühlung" wert={zahl(soll.rueckkuehlungKw)} einheit="kW"
          />
          <KpiCard
            titel="Behältervolumen" wert={zahl(soll.speicherM3, 1)} einheit="m³"
          />
          <KpiCard
            titel="Pumpen" wert={`${soll.pumpenHeizung} / ${soll.pumpenKaelte}`}
            fussnote="Heizung / Kälte"
          />
          <KpiCard
            titel="RLT-Geräte" wert={zahl(soll.rltGeraete)}
            fussnote={`${zahl(soll.rltHeizleistungKw, 1)} kW Heizen · `
              + `${zahl(soll.rltKuehlleistungKw, 1)} kW Kühlen`}
          />
        </KpiReihe>
      </ContentSection>

      <ContentSection
        titel="Die Sollseite je System und Kategorie"
        beschreibung={'Was die Vorlage als GRENZE nennt („max. 3MW", „bis DN80"), '
          + 'steht in einer eigenen Spalte und nicht in der Summe: Aus einer '
          + 'Obergrenze eine installierte Leistung zu machen wäre falsch — sie '
          + 'wegzulassen hieße, dass 3.000 kW Eisspeicher spurlos verschwinden.'}
        nackt
      >
        <DataTable
          name="Sollseite"
          spalten={[
            { schluessel: 'system', titel: 'System', wert: (g) => g.system ?? '' },
            { schluessel: 'kategorie', titel: 'Kategorie',
              wert: (g) => g.kategorie ?? '' },
            { schluessel: 'zeilen', titel: 'Zeilen', art: 'zahl',
              wert: (g) => g.zeilen },
            { schluessel: 'stueck', titel: 'Stück', art: 'zahl',
              wert: (g) => g.stueck },
            { schluessel: 'leistung', titel: 'Leistung kW', art: 'zahl',
              wert: (g) => g.leistungKw,
              inhalt: (g) => (g.leistungKw > 0
                ? <>{zahl(g.leistungKw)}</>
                : <span className="schwach">—</span>) },
            { schluessel: 'grenze', titel: 'davon Obergrenze kW', art: 'zahl',
              wert: (g) => g.leistungGrenzeKw,
              inhalt: (g) => (g.leistungGrenzeKw > 0
                ? <>{zahl(g.leistungGrenzeKw)}</>
                : <span className="schwach">—</span>) },
            { schluessel: 'volumen', titel: 'Volumen m³', art: 'zahl',
              wert: (g) => g.volumenM3,
              inhalt: (g) => (g.volumenM3 > 0
                ? <>{zahl(g.volumenM3, 1)}</>
                : <span className="schwach">—</span>) },
          ]}
          zeilen={soll.gruppen}
          schluessel={(g) => `${g.system}|${g.kategorie}`}
          ausfuhr
          ausfuhrDateiname="sollseite-heizung-kaelte"
        />
      </ContentSection>

      <ContentSection
        titel="Wie weit die Liste den Bestand trifft"
        beschreibung={'Drei Wege, in dieser Reihenfolge: AKS, Standort, Name. Jede '
          + 'Zuordnung trägt den Weg, der sie getragen hat — eine Zuordnung ohne '
          + 'Weg ist keine. Es wird dabei KEINE AKS-Kennung geschrieben; die AKS '
          + 'ist eingefroren (F29).'}
      >
        <KpiReihe>
          <KpiCard titel="Zeilen gesamt"
                   wert={zahl(lauf.zeilenHk + lauf.zeilenFws + lauf.zeilenRlt)} />
          <KpiCard titel="über AKS" wert={zahl(lauf.zuordnungAks)} />
          <KpiCard titel="über Standort" wert={zahl(lauf.zuordnungStandort)} />
          <KpiCard titel="über Name" wert={zahl(lauf.zuordnungName)} />
          <KpiCard
            titel="ohne Zuordnung" wert={zahl(lauf.ohneZuordnung)}
            zustand={lauf.ohneZuordnung > 0 ? 'warnung' : 'gut'}
            zustandText={lauf.ohneZuordnung > 0
              ? 'im Modell nicht gefunden' : 'alle gefunden'}
          />
        </KpiReihe>

        {lauf.hinweis && <div className="hinweis">{lauf.hinweis}</div>}
      </ContentSection>

      <ContentSection
        titel="Stückzahlen — Liste gegen Modell"
        beschreibung={'Die Zahl, an der sich beide Quellen am schnellsten messen '
          + 'lassen. Steht rechts nichts, gibt es für diese Kategorie keine Regel, '
          + 'welche Bauteilart im Modell ihr entspricht — das ist eine Auskunft '
          + 'und keine Lücke.'}
        nackt
      >
        <DataTable
          name="Stückzahlen"
          spalten={[
            { schluessel: 'kategorie', titel: 'Kategorie der Vorlage',
              wert: (z) => z.kategorie },
            { schluessel: 'inListe', titel: 'in der Liste', art: 'zahl',
              wert: (z) => z.inListe },
            { schluessel: 'bauteilart', titel: 'Bauteilart im Modell',
              wert: (z) => z.bauteilart || '—' },
            { schluessel: 'imModell', titel: 'im Modell', art: 'zahl',
              wert: (z) => z.imModell ?? -1,
              inhalt: (z) => (z.imModell === null
                ? <span className="schwach">keine Regel</span>
                : <>{zahl(z.imModell)}</>) },
            { schluessel: 'diff', titel: 'Unterschied', art: 'zahl',
              wert: (z) => (z.imModell === null ? 0 : z.imModell - z.inListe),
              inhalt: (z) => (z.imModell === null
                ? <span className="schwach">—</span>
                : <>{z.imModell === z.inListe
                  ? <StatusBadge zustand="gut" klein text="gleich" />
                  : `${z.imModell - z.inListe > 0 ? '+' : ''}`
                    + `${zahl(z.imModell - z.inListe)}`}</>) },
          ]}
          zeilen={abw.stueckzahlen}
          schluessel={(z) => z.kategorie}
        />
      </ContentSection>

      {abw.nichtInListe.length > 0 && (
        <ContentSection
          titel={`Im Modell, aber nicht in der Liste — ${abw.nichtInListe.length} `
            + `von ${abw.anlagenImModell} Lüftungsanlagen`}
          beschreibung={'Die Gegenrichtung. Sie ist die, die sonst niemand '
            + 'aufschreibt — und gerade sie sagt, ob die Vorlage vollständig ist.'}
        >
          <p style={{ fontSize: 12 }}>
            {abw.nichtInListe.map((a) => a.anlage).join(' · ')}
          </p>
        </ContentSection>
      )}

      <ContentSection
        titel={`Die Liste — ${liste.zeilen.length} Zeilen`}
        nackt
      >
        <div className="filterzeile" style={{ marginBottom: 8 }}>
          <select value={quelle} onChange={(e) => setQuelle(e.target.value)}>
            <option value="">alle drei Tabellen der Vorlage</option>
            <option value="HK">Geräteliste Heizung/Kälte (Z###)</option>
            <option value="FWS">Frischwasserstationen (F###)</option>
            <option value="RLT">RLT-Geräte (R###)</option>
          </select>

          <label style={{ marginLeft: 12 }}>
            <input
              type="checkbox" checked={nurOffen}
              onChange={(e) => setNurOffen(e.target.checked)}
            />
            {' '}nur die im Modell nicht gefundenen
          </label>
        </div>

        <DataTable
          name="Sollgeräte"
          spalten={spalten}
          zeilen={liste.zeilen}
          schluessel={(z) => z.id}
          ausfuhr
          ausfuhrDateiname="geraeteliste-hk-soll"
          leer={(
            <EmptyState
              titel="Keine Zeile in dieser Auswahl."
              erklaerung="Der Filter oben schränkt die Liste ein."
            />
          )}
        />
      </ContentSection>
    </>
  );
}
